import React, { useState } from 'react';
import ProtectedLayout from '../components/ProtectedLayout';
import { Link } from 'react-router-dom';

const STATUS_MAP = {
  live:   { label: 'Live',   color: '#059669', bg: '#ECFDF5', icon: 'fa-circle-check' },
  wip:    { label: 'WIP',    color: '#D97706', bg: '#FFFBEB', icon: 'fa-clock' },
  planned:{ label: 'Planned', color: '#6366F1', bg: '#EEF2FF', icon: 'fa-calendar' },
  deprecated: { label: 'Deprecated', color: '#94A3B8', bg: '#F1F5F9', icon: 'fa-circle-xmark' },
};

const SECTIONS = [
  {
    title: 'Agent Quoting',
    items: [
      { label: 'Quote V1', link: '/quote', status: 'live', description: 'Original agent quoting tool with carrier selection and instant results.' },
      { label: 'Quote V2', link: '/quote-v2', status: 'live', description: 'Redesigned quoting flow with streamlined UX and improved carrier matching.' },
    ],
  },
  {
    title: 'Widgets',
    items: [
      { label: 'Widgets V1', link: '/settings/widgets', status: 'live', description: 'Basic widget builder with template selection, color picker, and form field management.' },
      { label: 'Widgets V2', link: '/settings/widgets-v2', status: 'live', description: 'Advanced builder with font selection, preset themes, A/B testing, version history, mobile preview, analytics placeholders, and duplicate widget.' },
    ],
  },
];

const VALIDATION_RULES = [
  {
    title: 'Date of Birth',
    scope: 'Widget Preview, Quote Forms',
    status: 'live',
    rules: [
      { field: 'Month (MM)', rule: '2-digit numeric input, range 01-12. Triggers numeric keypad on mobile via inputMode="numeric".' },
      { field: 'Day (DD)', rule: '2-digit numeric input, range 01-31 (contextual). Max days adjust per month: Feb = 28 (or 29 in leap years), Apr/Jun/Sep/Nov = 30, all others = 31.' },
      { field: 'Year (YYYY)', rule: '4-digit numeric input, range 1920 to current year. Shows warning if out of range.' },
      { field: 'Cross-field', rule: 'Changing month re-validates day (e.g., switching to Feb with day=31 triggers error). Changing year re-validates leap year for Feb.' },
      { field: 'Auto-advance', rule: 'Completing 2 digits in Month auto-focuses Day. Completing 2 digits in Day auto-focuses Year.' },
      { field: 'Mobile', rule: 'type="tel" + inputMode="numeric" ensures numeric keypad on iOS and Android.' },
    ],
  },
  {
    title: 'Phone Number',
    scope: 'Widget Preview, Branding Page (Agency phone, Account phone)',
    status: 'live',
    rules: [
      { field: 'Format', rule: 'Auto-formats as (xxx) xxx-xxxx while typing. Only digits accepted, capped at 10.' },
      { field: 'Length', rule: 'Must be exactly 10 digits. Error on blur if fewer than 10 digits entered.' },
      { field: 'Sequential', rule: 'Rejects sequential digits: 1234567890 or 0987654321.' },
      { field: 'Repeated', rule: 'Rejects all-same digits: e.g., 5555555555 (regex /^(.)\\1{9}$/).' },
      { field: 'Mobile', rule: 'type="tel" triggers phone keypad on mobile devices.' },
    ],
  },
  {
    title: 'Email Address',
    scope: 'Widget Preview',
    status: 'live',
    rules: [
      { field: 'Format', rule: 'Validated on blur with pattern: user@domain.tld where TLD is 2+ characters.' },
      { field: 'Regex', rule: '/^[^\\s@]+@[^\\s@]+\\.[^\\s@]{2,}$/ — rejects spaces, requires @ and valid domain.' },
      { field: 'Error display', rule: 'Red border on input + error message below field. Clears on next keystroke.' },
    ],
  },
];

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.planned;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: '600', color: s.color, background: s.bg, padding: '2px 8px', borderRadius: '20px' }}>
      <i className={`fas ${s.icon}`} style={{ fontSize: '9px' }} />{s.label}
    </span>
  );
}

export default function SitemapPage() {
  const [openRule, setOpenRule] = useState(null);

  return (
    <ProtectedLayout>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ marginBottom: '28px' }}>
          <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#0F172A', margin: 0 }}>Sitemap</h1>
          <p style={{ fontSize: '13px', color: '#64748B', margin: '4px 0 0' }}>Internal reference for pages, versions, validation rules, and feature status.</p>
        </div>

        {/* Pages */}
        {SECTIONS.map((section) => (
          <div key={section.title} style={{ marginBottom: '28px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>
              {section.title}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {section.items.map((item) => (
                <Link key={item.link} to={item.link} style={{ textDecoration: 'none' }} data-testid={`sitemap-link-${item.link.replace(/\//g, '-').slice(1)}`}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 18px', background: '#fff', border: '1px solid #E2E8F0', borderRadius: '10px', transition: 'border-color 150ms, box-shadow 150ms', cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#2D87F0'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(45,135,240,.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.boxShadow = 'none'; }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                        <span style={{ fontWeight: '600', fontSize: '14px', color: '#0F172A' }}>{item.label}</span>
                        <StatusBadge status={item.status} />
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748B', lineHeight: '1.4' }}>{item.description}</div>
                    </div>
                    <div style={{ fontSize: '11px', color: '#CBD5E1', fontFamily: 'monospace', flexShrink: 0 }}>{item.link}</div>
                    <i className="fas fa-arrow-right" style={{ fontSize: '11px', color: '#CBD5E1' }} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}

        {/* Validation Rules */}
        <div style={{ marginBottom: '28px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>
            Validation Rules
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {VALIDATION_RULES.map((rule) => {
              const isOpen = openRule === rule.title;
              return (
                <div key={rule.title} style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '10px', overflow: 'hidden', transition: 'border-color 150ms' }}>
                  <button type="button" onClick={() => setOpenRule(isOpen ? null : rule.title)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                        <span style={{ fontWeight: '600', fontSize: '14px', color: '#0F172A' }}>{rule.title}</span>
                        <StatusBadge status={rule.status} />
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748B' }}>{rule.scope}</div>
                    </div>
                    <div style={{ fontSize: '11px', color: '#CBD5E1', marginRight: '4px' }}>{rule.rules.length} rules</div>
                    <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} style={{ fontSize: '11px', color: '#94A3B8' }} />
                  </button>
                  {isOpen && (
                    <div style={{ padding: '0 18px 16px', borderTop: '1px solid #F1F5F9' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left', fontSize: '10px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '6px 10px', borderBottom: '1px solid #F1F5F9' }}>Field / Check</th>
                            <th style={{ textAlign: 'left', fontSize: '10px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '6px 10px', borderBottom: '1px solid #F1F5F9' }}>Rule</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rule.rules.map((r, i) => (
                            <tr key={i}>
                              <td style={{ fontSize: '12px', fontWeight: '600', color: '#334155', padding: '8px 10px', borderBottom: '1px solid #F8FAFC', verticalAlign: 'top', whiteSpace: 'nowrap' }}>{r.field}</td>
                              <td style={{ fontSize: '12px', color: '#64748B', padding: '8px 10px', borderBottom: '1px solid #F8FAFC', lineHeight: '1.5' }}>{r.rule}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </ProtectedLayout>
  );
}
